import { useEffect, useState, useRef, useCallback } from 'react';
import { useAtom } from 'jotai';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import RecommendedTopics from '../RightContentComponent/components/recommended-topics.component';
import FeaturedVoices from '../RightContentComponent/components/featured-voices.component';
import StoryPulseCompact from './StoryPulseCompact';
import CollapseIcon from '../../../assets/collapse-icon.svg';
import { Divider, Box } from '@chakra-ui/react';
import { openSideBarAtom } from '../../Header/HeaderNewsletter/atom';

const SIDEBAR_WIDTH = 280;
const EDGE_SWIPE_ZONE = 30; // pixels from left edge to trigger swipe-to-open
const SWIPE_THRESHOLD = 50; // minimum swipe distance to trigger open/close

const LeftContentComponent = ({ isFixed = false }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [openSidebar, setOpenSidebar] = useAtom(openSideBarAtom);

  // Swipe gesture state
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const sidebarX = useMotionValue(0);
  const backdropOpacity = useTransform(sidebarX, [-SIDEBAR_WIDTH, 0], [0, 0.3]);

  // Handle swipe-to-open from edge (only on mobile when sidebar is closed)
  useEffect(() => {
    if (typeof window === 'undefined' || !isFixed) return;

    // Only add edge swipe when sidebar is closed
    if (openSidebar) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      // Only trigger if touch starts near left edge
      if (touch.clientX <= EDGE_SWIPE_ZONE) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        setIsDragging(true);
        sidebarX.set(-SIDEBAR_WIDTH + touch.clientX);
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);

      // If vertical movement is greater, cancel the swipe (user is scrolling)
      if (deltaY > Math.abs(deltaX) && deltaY > 10) {
        setIsDragging(false);
        sidebarX.set(-SIDEBAR_WIDTH);
        return;
      }

      // Clamp the position between -SIDEBAR_WIDTH and 0
      const newX = Math.min(0, Math.max(-SIDEBAR_WIDTH, -SIDEBAR_WIDTH + touch.clientX));
      sidebarX.set(newX);
    };

    const handleTouchEnd = (e) => {
      if (!isDragging) return;

      const currentX = sidebarX.get();
      setIsDragging(false);

      // If dragged more than threshold, open the sidebar
      if (currentX > -SIDEBAR_WIDTH + SWIPE_THRESHOLD) {
        setOpenSidebar(true);
        sidebarX.set(0);
      } else {
        sidebarX.set(-SIDEBAR_WIDTH);
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isFixed, openSidebar, isDragging, sidebarX, setOpenSidebar]);

  // Reset sidebar position when it opens/closes via button
  useEffect(() => {
    if (openSidebar) {
      sidebarX.set(0);
    } else {
      sidebarX.set(-SIDEBAR_WIDTH);
    }
  }, [openSidebar, sidebarX]);

  // Desktop collapse logic
  useEffect(() => {
    if (typeof window === 'undefined' || isFixed) return;

    const updateCollapsed = () => {
      if (window.innerWidth < 1328) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    updateCollapsed();
    window.addEventListener('resize', updateCollapsed);

    return () => {
      window.removeEventListener('resize', updateCollapsed);
    };
  }, [isFixed]);

  // Handle drag on sidebar to close
  const handleDragEnd = useCallback(
    (event, info) => {
      // If dragged left past threshold, close sidebar
      if (info.offset.x < -SWIPE_THRESHOLD) {
        setOpenSidebar(false);
      }
    },
    [setOpenSidebar]
  );

  // Mobile: controlled by header's hamburger menu via openSideBarAtom
  if (isFixed) {
    return (
      <>
        {/* Edge swipe preview (shows partial sidebar while dragging from edge) */}
        {isDragging && !openSidebar && (
          <>
            <motion.div
              className="fixed inset-0 bg-black lg:hidden z-[90] mt-[58px] md:mt-[80px]"
              style={{ opacity: backdropOpacity }}
            />
            <motion.aside
              className="fixed inset-y-0 left-0 z-[95] flex flex-col pt-[58px] md:pt-[80px] bg-white w-[280px] border-r-2 border-[#D9D9D9] overflow-y-auto shadow-xl"
              style={{ x: sidebarX }}
            >
              <div className="flex flex-col gap-4 p-4">
                <Box>
                  <RecommendedTopics />
                </Box>
                <Divider borderColor="#D9D9D9" borderWidth="1px" />
                <Box>
                  <FeaturedVoices />
                </Box>
                <Divider borderColor="#D9D9D9" borderWidth="1px" />
                {/* <Box>
                  <StoryPulseCompact />
                </Box> */}
              </div>
            </motion.aside>
          </>
        )}

        {/* Full sidebar when open */}
        <AnimatePresence>
          {openSidebar && (
            <motion.aside
              initial={{ x: -SIDEBAR_WIDTH }}
              animate={{ x: 0 }}
              exit={{ x: -SIDEBAR_WIDTH }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              drag="x"
              dragConstraints={{ left: -SIDEBAR_WIDTH, right: 0 }}
              dragElastic={0.1}
              onDragEnd={handleDragEnd}
              className="fixed inset-y-0 left-0 z-[95] flex flex-col pt-[58px] md:pt-[80px] bg-white w-[280px] border-r-2 border-[#D9D9D9] overflow-y-auto shadow-xl touch-pan-y"
            >
              <div className="flex flex-col gap-4 p-4">
                <Box>
                  <RecommendedTopics />
                </Box>
                <Divider borderColor="#D9D9D9" borderWidth="1px" />
                <Box>
                  <FeaturedVoices />
                </Box>
                <Divider borderColor="#D9D9D9" borderWidth="1px" />
                {/* <Box>
                  <StoryPulseCompact />
                </Box> */}
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Desktop: local collapse state with toggle button
  const panelClasses = isCollapsed ? 'w-0 opacity-0 pointer-events-none' : 'w-full opacity-100';

  const asideWidth = isCollapsed ? 'w-[24px]' : 'w-[260px]';

  return (
    <aside
      className={`lg:flex lg:flex-col pt-3 relative h-full border-r-2 border-[#D9D9D9] transition-all duration-300 ${asideWidth}`}
    >
      <div className="sticky top-24 w-full ml-2 max-h-[calc(100vh-120px)] flex flex-col">
        <button
          type="button"
          aria-expanded={!isCollapsed}
          aria-controls="discovery-left-rail"
          onClick={() => setIsCollapsed((prev) => !prev)}
          className="absolute -right-3 top-2 z-10 border border-[#E6E7EB] bg-white rounded-md shadow-[0_2px_8px_rgba(0,0,0,0.12)] w-8 h-8 flex items-center justify-center transition-colors hover:border-growth-green-500"
        >
          <img
            src={CollapseIcon}
            alt={isCollapsed ? 'Show left rail' : 'Hide left rail'}
            className={`w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          />
        </button>

        <div
          id="discovery-left-rail"
          className={`transition-all duration-300 overflow-y-auto overflow-x-hidden pr-2 ${panelClasses}`}
        >
          <div
            className={`w-[240px] flex flex-col gap-4 pb-4 ${
              isCollapsed ? 'opacity-0' : 'opacity-100'
            } transition-all duration-300`}
          >
            <Box>
              <RecommendedTopics />
            </Box>
            <Divider borderColor="#D9D9D9" borderWidth="1px" />
            <Box>
              <FeaturedVoices />
            </Box>
            {/* Story Pulse - shows when right sidebar is hidden (below xl) */}
            {/* <div className="xl:hidden">
              <Divider borderColor="#D9D9D9" borderWidth="1px" mb={4} />
              <Box>
                <StoryPulseCompact />
              </Box>
            </div> */}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default LeftContentComponent;
